import { Button, Page, TextField } from '../components/forms';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Banner, BlockStack, Card, DataTable, InlineStack } from '@shopify/polaris';
import { useTransferClient, useTransferRead, type TransferClient } from '../components/transfers/useTransferClient';
import { TransferFeedback } from '../components/transfers/TransferFeedback';
import { TransferDraft } from '../components/transfers/TransferDraft';
import { TransferMovement } from '../components/transfers/TransferMovement';
import { TransferHistory } from '../components/transfers/TransferHistory';
import { TransferHoldings } from '../components/transfers/TransferHoldings';
import { TransferIdentities } from '../components/transfers/TransferIdentities';
import { transit, unshipped, type TransferDetail } from '../components/transfers/types';
export default function TransferOrderDetail() {
  const { transferId } = useParams();
  const client = useTransferClient();
  return <Detail key={`${client.key}:${transferId}`} client={client} id={Number(transferId)} />;
}
function Detail({ client, id }: { client: TransferClient; id: number }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [draft, setDraft] = useState<TransferDetail | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [identityItem, setIdentityItem] = useState<number | null>(null);
  const { data, error } = useTransferRead<TransferDetail>(
    client,
    `/${id}${cursor ? '?cursor=' + cursor : ''}`,
    refresh,
  );
  const t = data?.transfer;
  const edit = async () => {
    setDraftLoading(true);
    setDraftError('');
    try {
      const all = await client.read<TransferDetail>(`/${id}`);
      let after = all.nextCursor;
      while (after && all.items.length < 500) {
        const page = await client.read<TransferDetail>(`/${id}?cursor=${after}`);
        if (page.transfer.revision !== all.transfer.revision)
          throw new Error('This draft changed while loading. Refresh and review it again.');
        all.items.push(...page.items);
        after = page.nextCursor;
      }
      if (after || all.items.length > 500)
        throw new Error('This older draft exceeds 500 lines. It needs reconciliation before replacement.');
      setDraft({ ...all, nextCursor: null });
    } catch (err) {
      setDraftError(err instanceof Error ? err.message : 'Could not load the draft.');
    } finally {
      setDraftLoading(false);
    }
  };
  const editable =
    data?.canEdit &&
    t &&
    ['draft', 'pending', 'approved'].includes(t.status) &&
    !t.shipped &&
    !t.received &&
    !t.damaged &&
    !t.cancelled &&
    !t.lost &&
    !t.returned;
  const active = data?.canEdit && t?.lifecycle_version === 1 && !['received', 'closed', 'cancelled'].includes(t.status);
  return (
    <Page
      title={t?.reference || 'Warehouse transfer'}
      backAction={{ content: 'Transfers', onAction: () => navigate('/transfer-orders') }}
    >
      <BlockStack gap="400">
        <TransferFeedback client={client} />
        {error && <Banner tone="critical">{error}</Banner>}
        {draftError && <Banner tone="critical">{draftError}</Banner>}
        <InlineStack gap="200">
          <Button
            disabled={client.locked || draftLoading || !!draft}
            onClick={() => {
              setRefresh((x) => x + 1);
              setConfirmCancel(false);
            }}
          >
            Refresh review
          </Button>
          {editable && !draft && (
            <Button disabled={client.locked || draftLoading} loading={draftLoading} onClick={() => void edit()}>
              {t?.lifecycle_version === 1 ? 'Edit unshipped draft' : 'Review and replace older draft'}
            </Button>
          )}
        </InlineStack>
        {!data && !error && <p>Loading transfer…</p>}
        {t && data && (
          <>
            <Card>
              <BlockStack gap="300">
                <h2>
                  {t.from_warehouse_name} to {t.to_warehouse_name}
                </h2>
                <p>
                  Status: {t.status.replace(/_/g, ' ')}. Priority:{' '}
                  {['Normal', 'High', 'Urgent'][t.priority] || 'Normal'}.
                </p>
                <p>
                  All {t.item_count} lines: {t.requested} planned; {t.shipped} shipped; {t.received} received usable;{' '}
                  {t.damaged} arrived damaged; {t.shipped - t.received - t.damaged - t.lost - t.returned} in transit;{' '}
                  {t.cancelled} cancelled; {t.returned} returned to source; {t.lost} lost.
                </p>
                <p>
                  Planned departure: {t.expected_ship_date || 'Not set'}. Planned arrival:{' '}
                  {t.expected_arrival_date || 'Not set'}.
                </p>
                {t.reason && <p>Reason: {t.reason}</p>}
                {t.notes && <p>Notes: {t.notes}</p>}
                {t.carrier && (
                  <p>
                    Carrier or van: {t.carrier}. Tracking: {t.tracking_number || 'Not set'}.
                  </p>
                )}
                {t.lifecycle_version !== 1 && (
                  <Banner tone="warning">
                    This older transfer has unverified stock movements. Physical changes are blocked until
                    reconciliation. Only an unshipped draft can be replaced after review.
                  </Banner>
                )}
                {t.invalid_items > 0 && (
                  <Banner tone="critical">
                    {t.invalid_items} lines need their product or bins reviewed. Some unsafe older lines cannot be
                    displayed. Replacing a draft requires reviewing all quantities.
                  </Banner>
                )}
              </BlockStack>
            </Card>
            {draft ? (
              <TransferDraft client={client} initial={draft} close={() => setDraft(null)} />
            ) : (
              <>
                <Card>
                  <BlockStack gap="300">
                    <h2>Transfer items</h2>
                    {data.items.filter(item => !item.untracked).map(item => <div key={item.id}><Button onClick={() => setIdentityItem(identityItem === item.id ? null : item.id)}>Serial and batch history for {item.product_sku || item.product_title}</Button>{identityItem === item.id && <TransferIdentities client={client} transfer={t.id} item={item.id} revision={t.revision} action="history"/>}</div>)}
                    <DataTable
                      columnContentTypes={[
                        'text',
                        'text',
                        'numeric',
                        'numeric',
                        'numeric',
                        'numeric',
                        'numeric',
                        'text',
                      ]}
                      headings={[
                        'Product',
                        'Source bin',
                        'Unshipped',
                        'In transit',
                        'Usable received',
                        'Damaged received',
                        'Available at source',
                        'Inventory update',
                      ]}
                      rows={data.items.map((item) => [
                        item.product_sku || item.product_title,
                        item.from_location_name || 'Needs review',
                        unshipped(item),
                        transit(item),
                        item.quantity_received,
                        item.quantity_damaged,
                        item.available,
                        !item.shopify_mapped
                          ? 'No Shopify product mapping'
                          : item.inventory_job_status === 'success'
                            ? 'Confirmed in Shopify'
                            : item.inventory_job_status === 'unverified_success'
                              ? 'Older success, unverified'
                            : item.inventory_job_status?.replace(/_/g, ' ') || 'No recorded inventory job',
                      ])}
                    />
                    <p>
                      {t.inventory_publication === 'not_connected'
                        ? 'No Shopify store is connected. This transfer updates local stock only.'
                        : t.inventory_publication === 'disabled'
                          ? 'Shopify inventory updates are paused in the store settings.'
                          : t.inventory_publication === 'settings_need_review'
                            ? 'Shopify settings need review before inventory updates can run.'
                            : 'A Shopify store is connected. Inventory updates still depend on product and warehouse mappings.'}
                    </p>
                    <p>
                      Up to 100 lines per page. Totals above cover the whole transfer. Shopify inventory jobs can
                      include other stock changes. A completed job does not prove that this transfer was published.
                    </p>
                    {(!t.source_mapped || !t.destination_mapped) && (
                      <p>One or both warehouses have no Shopify location mapping. Local stock movements still work.</p>
                    )}
                    <InlineStack gap="200">
                      <Button disabled={client.locked || !cursor} onClick={() => setCursor(null)}>
                        First item page
                      </Button>
                      <Button disabled={client.locked || !data.nextCursor} onClick={() => setCursor(data.nextCursor)}>
                        Next item page
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Card>
                {active && (
                  <TransferMovement key={`${t.revision}:${cursor}`} client={client} transfer={t} items={data.items} />
                )}
                {active && t.requested > t.shipped + t.cancelled && (
                  <Card>
                    <details data-form-scope="">
                      <summary>Cancel the unshipped remainder</summary>
                      <BlockStack gap="300">
                        <p>
                          Cancel {t.requested - t.shipped - t.cancelled} units still planned for shipment. Goods already
                          in transit still need to be received, returned or recorded lost.
                        </p>
                        <TextField
                          label="Cancellation reason"
                          requiredIndicator value={cancelReason}
                          onChange={setCancelReason}
                          maxLength={500}
                          disabled={client.locked || confirmCancel}
                          autoComplete="off"
                        />
                        {confirmCancel ? (
                          <InlineStack gap="200">
                            <Button validate
                              tone="critical"
                              disabled={client.locked || cancelReason.trim().length < 3}
                              onClick={() =>
                                void client.submit(`/${id}/cancel`, { revision: t.revision, reason: cancelReason })
                              }
                            >
                              Confirm remainder cancellation
                            </Button>
                            <Button disabled={client.locked} onClick={() => setConfirmCancel(false)}>
                              Change cancellation
                            </Button>
                          </InlineStack>
                        ) : (
                          <Button
                            disabled={client.locked || cancelReason.trim().length < 3}
                            onClick={() => setConfirmCancel(true)}
                          >
                            Review cancellation
                          </Button>
                        )}
                      </BlockStack>
                    </details>
                  </Card>
                )}
                {t.lifecycle_version === 1 && (
                  <TransferHoldings client={client} transfer={t} canEdit={data.canEdit} refresh={refresh} />
                )}
              </>
            )}
          </>
        )}
        <TransferHistory client={client} id={id} refresh={refresh} />
      </BlockStack>
    </Page>
  );
}
